import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { ArrowsClockwise, TrendUp, TrendDown, CircleNotch, CaretDown } from "@phosphor-icons/react";
import SectorStocksExpander from "./SectorStocksExpander";

const STRENGTH_COLORS = {
  "Very Strong": { chip: "bg-pos-soft text-pos border-pos-soft", bar: "hsl(var(--signal-positive))" },
  Strong: { chip: "bg-pos-soft text-pos border-pos-soft", bar: "hsl(var(--signal-positive) / 0.7)" },
  Neutral: { chip: "bg-surface-hover text-secondary-fg border-subtle", bar: "hsl(var(--text-muted) / 0.4)" },
  Weak: { chip: "bg-neg-soft text-neg border-neg-soft", bar: "hsl(var(--signal-negative) / 0.6)" },
  "Very Weak": { chip: "bg-neg-soft text-neg border-neg-soft", bar: "hsl(var(--signal-negative))" },
};

const STALE_AFTER_MS = 10 * 60 * 1000; // 10 minutes

export default function SectorScreener() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null); // sector symbol or null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sectors");
      setData(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("sectors load failed:", e);
    }
    finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sectors");
        setData(data);
        // If stale, auto-refresh in background
        const age = data?.snapshot_at ? Date.now() - new Date(data.snapshot_at).getTime() : Infinity;
        if (age > STALE_AFTER_MS) {
          setRefreshing(true);
          const fresh = await api.post("/sectors/refresh");
          setData(fresh.data);
          setRefreshing(false);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("initial sectors fetch failed:", e);
      }
      finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/sectors/refresh");
      setData(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("sectors refresh failed:", e);
    }
    finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 border border-subtle bg-surface rounded-sm flex items-center gap-3" data-testid="sector-loading">
        <CircleNotch className="animate-spin text-mod" size={18} weight="bold" />
        <div className="text-sm text-secondary-fg">Loading sector strength…</div>
      </div>
    );
  }

  const sectors = data?.sectors || [];
  const maxAbs = Math.max(1, ...sectors.map((s) => Math.abs(s.change_pct)));
  const snapAt = data?.snapshot_at
    ? new Date(data.snapshot_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  return (
    <div data-testid="sector-screener">
      <div className="flex items-baseline justify-between mb-4 border-b border-subtle pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg">
            /// Sector Strength
          </div>
          <div className="text-xs text-muted-fg mt-1">
            Snapshot at <span className="font-mono text-primary-fg">{snapAt}</span> · auto-refresh daily at 09:25 IST
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          data-testid="sector-refresh"
          className="px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest hover:bg-surface-hover text-secondary-fg hover:text-primary-fg transition flex items-center gap-2"
        >
          {refreshing ? (
            <CircleNotch size={12} weight="bold" className="animate-spin" />
          ) : (
            <ArrowsClockwise size={12} weight="bold" />
          )}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sectors.map((s, i) => {
          const style = STRENGTH_COLORS[s.strength] || STRENGTH_COLORS.Neutral;
          const pos = s.change_pct >= 0;
          const widthPct = Math.min(100, (Math.abs(s.change_pct) / maxAbs) * 100);
          const isOpen = expanded === s.symbol;
          return (
            <motion.div
              key={s.symbol}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.02 }}
              className="border border-subtle rounded-sm bg-surface overflow-hidden"
              data-testid={`sector-row-${s.symbol}`}
            >
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : s.symbol)}
                className="w-full p-4 relative text-left hover:bg-surface-hover transition"
                data-testid={`sector-toggle-${s.symbol}`}
                aria-expanded={isOpen}
              >
                {/* Bar fill */}
                <div
                  className="absolute left-0 top-0 bottom-0 opacity-10 pointer-events-none"
                  style={{ width: `${widthPct}%`, background: style.bar }}
                />
                <div className="relative flex items-center justify-between gap-4">
                  <div className="min-w-0 flex items-center gap-2">
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="shrink-0 text-muted-fg"
                    >
                      <CaretDown size={12} weight="bold" />
                    </motion.span>
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-primary-fg tracking-wide truncate">{s.name}</div>
                      <div className="text-[10px] font-mono text-muted-fg">
                        close ₹{s.prev_close.toLocaleString("en-IN")} → ₹{s.current.toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border ${style.chip}`}>
                      {s.strength}
                    </span>
                    <div
                      className={`font-mono text-lg tabular-nums flex items-center gap-1 w-24 justify-end ${
                        pos ? "text-pos" : "text-neg"
                      }`}
                    >
                      {pos ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                      {pos ? "+" : ""}
                      {s.change_pct.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && <SectorStocksExpander sectorSymbol={s.symbol} sectorName={s.name} />}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {sectors.length === 0 && (
        <div className="p-8 border border-subtle bg-surface rounded-sm text-center text-sm text-muted-fg">
          No sector data available yet. Click Refresh.
        </div>
      )}
    </div>
  );
}
